import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, ExternalLink, Trash2, Plus, Loader2, Upload } from "lucide-react";
import { api, formatApiError, imgSrc, uploadImage } from "@/lib/api";
import { LogoMark } from "@/components/Logo";

const TABS = [
  ["enquiries", "Enquiries"],
  ["packages", "Packages"],
  ["appplans", "App Plans"],
  ["portfolio", "Portfolio"],
  ["site", "Site & Form"],
  ["contact", "Contact Details"],
];

const inputCls =
  "w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:border-[#00F0FF]/60 focus:outline-none focus:ring-2 focus:ring-[#00F0FF]/30";

const Field = ({ label, children }) => (
  <div>
    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-widest text-slate-500">{label}</label>
    {children}
  </div>
);

export default function Admin() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState("enquiries");
  const [content, setContent] = useState(null);
  const [enquiries, setEnquiries] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadEnquiries = () =>
    api.get("/admin/enquiries").then((r) => setEnquiries(r.data)).catch(() => setEnquiries([]));

  useEffect(() => {
    api.get("/auth/me")
      .then((r) => {
        setUser(r.data);
        loadEnquiries();
      })
      .catch(() => navigate("/admin/login"));
    api.get("/content").then((r) => setContent(r.data)).catch(() => {});
  }, [navigate]);

  const logout = async () => {
    localStorage.removeItem("tarbyte_admin_token");
    try { await api.post("/auth/logout"); } catch {}
    navigate("/admin/login");
  };

  const saveContent = async () => {
    setSaving(true);
    try {
      await api.put("/admin/content", content);
      toast.success("Saved — changes are live on the site.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const updatePkg = (list, idx, key, value) => {
    const next = { ...content };
    next[list] = next[list].map((p, i) => (i === idx ? { ...p, [key]: value } : p));
    setContent(next);
  };

  const updatePortfolio = (idx, key, value) => {
    const next = { ...content };
    next.portfolio = next.portfolio.map((p, i) => (i === idx ? { ...p, [key]: value } : p));
    setContent(next);
  };

  const updateSite = (key, value) =>
    setContent({ ...content, site: { ...content.site, [key]: value } });

  const updateFormOptions = (key, value) =>
    setContent({ ...content, form_options: { ...content.form_options, [key]: value } });

  const [editing, setEditing] = useState(null);

  const saveEnquiry = async () => {
    try {
      await api.put(`/admin/enquiries/${editing.id}`, {
        name: editing.name, business_name: editing.business_name, phone: editing.phone,
        email: editing.email, service: editing.service, budget: editing.budget, message: editing.message,
      });
      setEnquiries((prev) => prev.map((en) => (en.id === editing.id ? { ...en, ...editing } : en)));
      setEditing(null);
      toast.success("Enquiry updated.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const setEnquiryStatus = async (id, status) => {
    try {
      await api.patch(`/admin/enquiries/${id}`, { status });
      setEnquiries((prev) => prev.map((en) => (en.id === id ? { ...en, status } : en)));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteEnquiry = async (id) => {
    if (!window.confirm("Delete this enquiry permanently?")) return;
    try {
      await api.delete(`/admin/enquiries/${id}`);
      setEnquiries((prev) => prev.filter((en) => en.id !== id));
      toast.success("Enquiry deleted.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#050811]">
        <Loader2 className="animate-spin text-[#00F0FF]" size={28} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050811] text-white" data-testid="admin-dashboard">
      <header className="border-b border-white/10 bg-black/60 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            <LogoMark size={30} />
            <span className="font-display font-extrabold text-white">
              TAR<span className="text-[#00F0FF]">BYTE</span>
              <span className="ml-3 rounded-full border border-white/15 px-3 py-0.5 text-xs font-semibold text-slate-400">Admin</span>
            </span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" data-testid="admin-view-site-link" className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-[#00F0FF]">
              View site <ExternalLink size={14} />
            </Link>
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              className="flex items-center gap-1.5 rounded-full border border-white/15 px-4 py-1.5 text-sm text-slate-300 hover:border-red-400/60 hover:text-red-300"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-6 py-8">
        <div className="mb-8 flex flex-wrap gap-2">
          {TABS.map(([id, label]) => (
            <button
              key={id}
              data-testid={`admin-tab-${id}`}
              onClick={() => setTab(id)}
              className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors duration-200 ${
                tab === id ? "bg-[#00F0FF] text-[#050811]" : "border border-white/15 text-slate-300 hover:border-[#00F0FF]/50"
              }`}
            >
              {label}
              {id === "enquiries" && enquiries && (
                <span className="ml-2 rounded-full bg-black/20 px-2 py-0.5 text-xs">
                  {enquiries.filter((e) => e.status === "new").length}
                </span>
              )}
            </button>
          ))}
        </div>

        {tab === "enquiries" && (
          <div data-testid="admin-enquiries-panel" className="space-y-4">
            {enquiries === null ? (
              <Loader2 className="animate-spin text-[#00F0FF]" size={24} />
            ) : enquiries.length === 0 ? (
              <p className="rounded-2xl border border-dashed border-white/15 p-10 text-center text-sm text-slate-500">
                No enquiries yet. New submissions from the contact form will appear here and be emailed to you.
              </p>
            ) : (
              enquiries.map((en) => (
                <div key={en.id} data-testid={`enquiry-card-${en.id}`} className="rounded-2xl border border-white/10 bg-[#0A0F1C] p-6">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="font-display text-base font-bold text-white">
                        {en.name}
                        {en.business_name ? <span className="ml-2 text-sm font-medium text-slate-400">· {en.business_name}</span> : null}
                      </p>
                      <p className="mt-1 text-xs text-slate-500">{new Date(en.created_at).toLocaleString()}</p>
                    </div>
                    <span
                      data-testid={`enquiry-status-${en.id}`}
                      className={`rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider ${
                        en.status === "new" ? "bg-[#00F0FF]/15 text-[#00F0FF]" : "bg-white/10 text-slate-400"
                      }`}
                    >
                      {en.status}
                    </span>
                  </div>
                  {editing?.id === en.id ? (
                    <div className="mt-4 space-y-3" data-testid={`enquiry-edit-form-${en.id}`}>
                      <div className="grid gap-3 sm:grid-cols-2">
                        <input className={inputCls} data-testid={`enquiry-edit-name-${en.id}`} value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="Name" />
                        <input className={inputCls} value={editing.business_name || ""} onChange={(e) => setEditing({ ...editing, business_name: e.target.value })} placeholder="Business name" />
                        <input className={inputCls} value={editing.phone} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} placeholder="Phone / WhatsApp" />
                        <input className={inputCls} value={editing.email} onChange={(e) => setEditing({ ...editing, email: e.target.value })} placeholder="Email" />
                        <input className={inputCls} value={editing.service} onChange={(e) => setEditing({ ...editing, service: e.target.value })} placeholder="Service" />
                        <input className={inputCls} value={editing.budget || ""} onChange={(e) => setEditing({ ...editing, budget: e.target.value })} placeholder="Budget" />
                      </div>
                      <textarea rows={3} className={inputCls} value={editing.message} onChange={(e) => setEditing({ ...editing, message: e.target.value })} placeholder="Project details" />
                      <div className="flex gap-2">
                        <button data-testid={`enquiry-save-${en.id}`} onClick={saveEnquiry} className="rounded-full bg-[#00F0FF] px-5 py-1.5 text-xs font-bold text-[#050811]">
                          Save
                        </button>
                        <button data-testid={`enquiry-cancel-${en.id}`} onClick={() => setEditing(null)} className="rounded-full border border-white/15 px-5 py-1.5 text-xs font-semibold text-slate-300">
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="mt-4 grid gap-2 text-sm text-slate-300 sm:grid-cols-2">
                        <p><span className="text-slate-500">Email:</span> <a className="text-[#00F0FF]" href={`mailto:${en.email}`}>{en.email}</a></p>
                        <p><span className="text-slate-500">Phone:</span> {en.phone}</p>
                        <p><span className="text-slate-500">Service:</span> {en.service}</p>
                        <p><span className="text-slate-500">Budget:</span> {en.budget || "—"}</p>
                      </div>
                      <p className="mt-3 rounded-xl bg-white/5 p-4 text-sm leading-relaxed text-slate-300">{en.message}</p>
                    </>
                  )}
                  <div className="mt-4 flex flex-wrap gap-2">
                    {en.status === "new" && editing?.id !== en.id && (
                      <button data-testid={`enquiry-read-${en.id}`} onClick={() => setEnquiryStatus(en.id, "read")} className="rounded-full border border-white/15 px-4 py-1.5 text-xs font-semibold text-slate-300 hover:border-[#00F0FF]/50 hover:text-[#00F0FF]">
                        Mark read
                      </button>
                    )}
                    {editing?.id !== en.id && (
                      <>
                        <button data-testid={`enquiry-replied-${en.id}`} onClick={() => setEnquiryStatus(en.id, "replied")} className="rounded-full border border-white/15 px-4 py-1.5 text-xs font-semibold text-slate-300 hover:border-[#00F0FF]/50 hover:text-[#00F0FF]">
                          Mark replied
                        </button>
                        <button data-testid={`enquiry-edit-${en.id}`} onClick={() => setEditing({ ...en })} className="rounded-full border border-white/15 px-4 py-1.5 text-xs font-semibold text-slate-300 hover:border-[#00F0FF]/50 hover:text-[#00F0FF]">
                          Edit
                        </button>
                      </>
                    )}
                    <button data-testid={`enquiry-delete-${en.id}`} onClick={() => deleteEnquiry(en.id)} className="flex items-center gap-1 rounded-full border border-white/15 px-4 py-1.5 text-xs font-semibold text-slate-300 hover:border-red-400/60 hover:text-red-300">
                      <Trash2 size={12} /> Delete
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {tab === "packages" && content && (
          <div data-testid="admin-packages-panel" className="space-y-8">
            {content.packages.map((pkg, i) => (
              <div key={pkg.id} className="rounded-2xl border border-white/10 bg-[#0A0F1C] p-6" data-testid={`admin-package-${pkg.id}`}>
                <div className="grid gap-4 sm:grid-cols-3">
                  <Field label="Package name">
                    <input className={inputCls} data-testid={`admin-package-name-${pkg.id}`} value={pkg.name} onChange={(e) => updatePkg("packages", i, "name", e.target.value)} />
                  </Field>
                  <Field label="Price (e.g. ₹9,999)">
                    <input className={inputCls} data-testid={`admin-package-price-${pkg.id}`} value={pkg.price} onChange={(e) => updatePkg("packages", i, "price", e.target.value)} />
                  </Field>
                  <Field label="Period">
                    <input className={inputCls} value={pkg.period} onChange={(e) => updatePkg("packages", i, "period", e.target.value)} />
                  </Field>
                </div>
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  <Field label="Tagline">
                    <input className={inputCls} value={pkg.tagline} onChange={(e) => updatePkg("packages", i, "tagline", e.target.value)} />
                  </Field>
                  <Field label="Maintenance note">
                    <input className={inputCls} value={pkg.maintenance} onChange={(e) => updatePkg("packages", i, "maintenance", e.target.value)} />
                  </Field>
                </div>
                <div className="mt-4">
                  <Field label="Features (one per line)">
                    <textarea rows={6} className={inputCls} data-testid={`admin-package-features-${pkg.id}`} value={pkg.features.join("\n")} onChange={(e) => updatePkg("packages", i, "features", e.target.value.split("\n"))} />
                  </Field>
                </div>
                <label className="mt-4 flex items-center gap-2 text-sm text-slate-300">
                  <input type="checkbox" data-testid={`admin-package-highlight-${pkg.id}`} checked={!!pkg.highlight} onChange={(e) => updatePkg("packages", i, "highlight", e.target.checked)} className="h-4 w-4 accent-[#00F0FF]" />
                  Highlight as "Most Popular"
                </label>
              </div>
            ))}

            <h3 className="font-display text-lg font-bold text-white">Maintenance plans</h3>
            {content.maintenance_plans.map((plan, i) => (
              <div key={plan.id} className="rounded-2xl border border-white/10 bg-[#0A0F1C] p-6" data-testid={`admin-plan-${plan.id}`}>
                <div className="grid gap-4 sm:grid-cols-3">
                  <Field label="Plan name">
                    <input className={inputCls} value={plan.name} onChange={(e) => updatePkg("maintenance_plans", i, "name", e.target.value)} />
                  </Field>
                  <Field label="Price (e.g. ₹1,999)">
                    <input className={inputCls} data-testid={`admin-plan-price-${plan.id}`} value={plan.price} onChange={(e) => updatePkg("maintenance_plans", i, "price", e.target.value)} />
                  </Field>
                  <Field label="Period">
                    <input className={inputCls} value={plan.period} onChange={(e) => updatePkg("maintenance_plans", i, "period", e.target.value)} />
                  </Field>
                </div>
                <div className="mt-4">
                  <Field label="Features (one per line)">
                    <textarea rows={5} className={inputCls} value={plan.features.join("\n")} onChange={(e) => updatePkg("maintenance_plans", i, "features", e.target.value.split("\n"))} />
                  </Field>
                </div>
              </div>
            ))}
            <SaveBar saving={saving} onSave={saveContent} testId="admin-save-packages-btn" />
          </div>
        )}

        {tab === "appplans" && content && (
          <div data-testid="admin-appplans-panel" className="space-y-8">
            {content.app_plans.map((plan, i) => (
              <div key={plan.id} className="rounded-2xl border border-white/10 bg-[#0A0F1C] p-6" data-testid={`admin-appplan-${plan.id}`}>
                <div className="grid gap-4 sm:grid-cols-3">
                  <Field label="Plan name">
                    <input className={inputCls} data-testid={`admin-appplan-name-${plan.id}`} value={plan.name} onChange={(e) => updatePkg("app_plans", i, "name", e.target.value)} />
                  </Field>
                  <Field label="Tier label (e.g. Recommended)">
                    <input className={inputCls} value={plan.tier} onChange={(e) => updatePkg("app_plans", i, "tier", e.target.value)} />
                  </Field>
                  <Field label="Price (e.g. ₹14,999)">
                    <input className={inputCls} data-testid={`admin-appplan-price-${plan.id}`} value={plan.price} onChange={(e) => updatePkg("app_plans", i, "price", e.target.value)} />
                  </Field>
                </div>
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  <Field label="Price note (e.g. One-time development fee)">
                    <input className={inputCls} value={plan.price_note} onChange={(e) => updatePkg("app_plans", i, "price_note", e.target.value)} />
                  </Field>
                  <Field label='List label (e.g. "Includes" / "May include")'>
                    <input className={inputCls} value={plan.includes_label} onChange={(e) => updatePkg("app_plans", i, "includes_label", e.target.value)} />
                  </Field>
                </div>
                <div className="mt-4">
                  <Field label="Tagline">
                    <input className={inputCls} value={plan.tagline} onChange={(e) => updatePkg("app_plans", i, "tagline", e.target.value)} />
                  </Field>
                </div>
                <div className="mt-4">
                  <Field label="Features (one per line)">
                    <textarea rows={7} className={inputCls} data-testid={`admin-appplan-features-${plan.id}`} value={plan.features.join("\n")} onChange={(e) => updatePkg("app_plans", i, "features", e.target.value.split("\n"))} />
                  </Field>
                </div>
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  <Field label="Examples line (optional)">
                    <input className={inputCls} value={plan.examples} onChange={(e) => updatePkg("app_plans", i, "examples", e.target.value)} />
                  </Field>
                  <Field label="Note box (optional)">
                    <input className={inputCls} value={plan.note} onChange={(e) => updatePkg("app_plans", i, "note", e.target.value)} />
                  </Field>
                </div>
                <label className="mt-4 flex items-center gap-2 text-sm text-slate-300">
                  <input type="checkbox" data-testid={`admin-appplan-highlight-${plan.id}`} checked={!!plan.highlight} onChange={(e) => updatePkg("app_plans", i, "highlight", e.target.checked)} className="h-4 w-4 accent-[#00F0FF]" />
                  Highlight as recommended
                </label>
              </div>
            ))}

            <h3 className="font-display text-lg font-bold text-white">App care plans (monthly)</h3>
            {content.app_care_plans.map((plan, i) => (
              <div key={plan.id} className="rounded-2xl border border-white/10 bg-[#0A0F1C] p-6" data-testid={`admin-appcare-${plan.id}`}>
                <div className="grid gap-4 sm:grid-cols-3">
                  <Field label="Plan name">
                    <input className={inputCls} value={plan.name} onChange={(e) => updatePkg("app_care_plans", i, "name", e.target.value)} />
                  </Field>
                  <Field label="Price (e.g. ₹1,999)">
                    <input className={inputCls} data-testid={`admin-appcare-price-${plan.id}`} value={plan.price} onChange={(e) => updatePkg("app_care_plans", i, "price", e.target.value)} />
                  </Field>
                  <Field label="Period">
                    <input className={inputCls} value={plan.period} onChange={(e) => updatePkg("app_care_plans", i, "period", e.target.value)} />
                  </Field>
                </div>
                <div className="mt-4">
                  <Field label="Features (one per line)">
                    <textarea rows={4} className={inputCls} value={plan.features.join("\n")} onChange={(e) => updatePkg("app_care_plans", i, "features", e.target.value.split("\n"))} />
                  </Field>
                </div>
              </div>
            ))}
            <SaveBar saving={saving} onSave={saveContent} testId="admin-save-appplans-btn" />
          </div>
        )}

        {tab === "portfolio" && content && (
          <div data-testid="admin-portfolio-panel" className="space-y-6">
            {content.portfolio.map((p, i) => (
              <div key={p.id} className="rounded-2xl border border-white/10 bg-[#0A0F1C] p-6" data-testid={`admin-portfolio-${p.id}`}>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Title">
                    <input className={inputCls} data-testid={`admin-portfolio-title-${p.id}`} value={p.title} onChange={(e) => updatePortfolio(i, "title", e.target.value)} />
                  </Field>
                  <Field label="Category">
                    <input className={inputCls} value={p.category} onChange={(e) => updatePortfolio(i, "category", e.target.value)} />
                  </Field>
                </div>
                <div className="mt-4">
                  <Field label="Description">
                    <textarea rows={2} className={inputCls} value={p.description} onChange={(e) => updatePortfolio(i, "description", e.target.value)} />
                  </Field>
                </div>
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  <ImageField label="Image" testId={`admin-portfolio-image-${p.id}`} value={p.image} onChange={(v) => updatePortfolio(i, "image", v)} />
                  <Field label="Live link (optional)">
                    <input className={inputCls} value={p.link} onChange={(e) => updatePortfolio(i, "link", e.target.value)} />
                  </Field>
                </div>
                <div className="mt-4 flex items-center justify-between">
                  <label className="flex items-center gap-2 text-sm text-slate-300">
                    <input type="checkbox" checked={!!p.isDemo} onChange={(e) => updatePortfolio(i, "isDemo", e.target.checked)} className="h-4 w-4 accent-[#00F0FF]" />
                    Label as Demo Project
                  </label>
                  <button
                    data-testid={`admin-portfolio-delete-${p.id}`}
                    onClick={() => setContent({ ...content, portfolio: content.portfolio.filter((x) => x.id !== p.id) })}
                    className="flex items-center gap-1 text-xs text-slate-400 hover:text-red-300"
                  >
                    <Trash2 size={13} /> Remove
                  </button>
                </div>
              </div>
            ))}
            <button
              data-testid="admin-portfolio-add-btn"
              onClick={() =>
                setContent({
                  ...content,
                  portfolio: [...content.portfolio, { id: `p${Date.now()}`, title: "", category: "", description: "", image: "", link: "", isDemo: true }],
                })
              }
              className="flex items-center gap-2 rounded-full border border-dashed border-white/25 px-5 py-2.5 text-sm font-semibold text-slate-300 hover:border-[#00F0FF]/60 hover:text-[#00F0FF]"
            >
              <Plus size={15} /> Add project
            </button>
            <SaveBar saving={saving} onSave={saveContent} testId="admin-save-portfolio-btn" />
          </div>
        )}

        {tab === "site" && content && (
          <div data-testid="admin-site-panel" className="max-w-2xl space-y-6">
            <div className="space-y-5 rounded-2xl border border-white/10 bg-[#0A0F1C] p-6">
              <h3 className="font-display text-lg font-bold text-white">Images</h3>
              <ImageField label="Hero image" testId="admin-hero-image-input" value={content.site.hero_image} onChange={(v) => updateSite("hero_image", v)} />
              <ImageField label="About section image" testId="admin-about-image-input" value={content.site.about_image} onChange={(v) => updateSite("about_image", v)} />
              <p className="text-xs text-slate-500">Portfolio images are edited per project in the Portfolio tab.</p>
            </div>

            <div className="space-y-5 rounded-2xl border border-white/10 bg-[#0A0F1C] p-6">
              <h3 className="font-display text-lg font-bold text-white">Hero text</h3>
              <div className="grid gap-4 sm:grid-cols-3">
                <Field label="Headline line 1">
                  <input className={inputCls} data-testid="admin-hero-line1-input" value={content.site.hero_line1} onChange={(e) => updateSite("hero_line1", e.target.value)} />
                </Field>
                <Field label="Headline line 2">
                  <input className={inputCls} value={content.site.hero_line2} onChange={(e) => updateSite("hero_line2", e.target.value)} />
                </Field>
                <Field label="Headline line 3 (highlighted)">
                  <input className={inputCls} value={content.site.hero_line3} onChange={(e) => updateSite("hero_line3", e.target.value)} />
                </Field>
              </div>
              <Field label="Hero intro paragraph">
                <textarea rows={3} className={inputCls} data-testid="admin-hero-subtext-input" value={content.site.hero_subtext} onChange={(e) => updateSite("hero_subtext", e.target.value)} />
              </Field>
            </div>

            <div className="space-y-5 rounded-2xl border border-white/10 bg-[#0A0F1C] p-6">
              <h3 className="font-display text-lg font-bold text-white">About section</h3>
              <Field label="Heading">
                <input className={inputCls} data-testid="admin-about-heading-input" value={content.site.about_heading} onChange={(e) => updateSite("about_heading", e.target.value)} />
              </Field>
              <Field label="Paragraphs (one per line)">
                <textarea rows={5} className={inputCls} value={content.site.about_paragraphs.join("\n")} onChange={(e) => updateSite("about_paragraphs", e.target.value.split("\n"))} />
              </Field>
            </div>

            <div className="space-y-5 rounded-2xl border border-white/10 bg-[#0A0F1C] p-6">
              <h3 className="font-display text-lg font-bold text-white">Enquiry form options</h3>
              <Field label='"Service needed" options (one per line)'>
                <textarea rows={6} className={inputCls} data-testid="admin-form-services-input" value={content.form_options.services.join("\n")} onChange={(e) => updateFormOptions("services", e.target.value.split("\n"))} />
              </Field>
              <Field label='"Budget range" options (one per line)'>
                <textarea rows={4} className={inputCls} data-testid="admin-form-budgets-input" value={content.form_options.budgets.join("\n")} onChange={(e) => updateFormOptions("budgets", e.target.value.split("\n"))} />
              </Field>
            </div>

            <SaveBar saving={saving} onSave={saveContent} testId="admin-save-site-btn" />
          </div>
        )}

        {tab === "contact" && content && (
          <div data-testid="admin-contact-panel" className="max-w-xl space-y-5 rounded-2xl border border-white/10 bg-[#0A0F1C] p-6">
            <Field label="Public email address">
              <input className={inputCls} data-testid="admin-contact-email-input" value={content.contact.email} onChange={(e) => setContent({ ...content, contact: { ...content.contact, email: e.target.value } })} />
            </Field>
            <Field label="WhatsApp number (digits only, with country code — e.g. 15551234567)">
              <input className={inputCls} data-testid="admin-contact-whatsapp-input" value={content.contact.whatsapp_number} onChange={(e) => setContent({ ...content, contact: { ...content.contact, whatsapp_number: e.target.value } })} />
            </Field>
            <Field label="WhatsApp number as shown on the site (e.g. +1 555 123 4567)">
              <input className={inputCls} data-testid="admin-contact-whatsapp-display-input" value={content.contact.whatsapp_display} onChange={(e) => setContent({ ...content, contact: { ...content.contact, whatsapp_display: e.target.value } })} />
            </Field>
            <Field label="Notification email — new enquiries are sent here">
              <input className={inputCls} data-testid="admin-owner-email-input" placeholder="you@yourbusiness.com" value={content.contact.owner_email} onChange={(e) => setContent({ ...content, contact: { ...content.contact, owner_email: e.target.value } })} />
            </Field>
            <p className="text-xs text-slate-500">
              Enquiries are always saved here in the admin panel. Set the notification email above to also receive each enquiry in your inbox. Replies go directly to the visitor.
            </p>
            <SaveBar saving={saving} onSave={saveContent} testId="admin-save-contact-btn" />
          </div>
        )}
      </div>
    </div>
  );
}

const ImageField = ({ label, value, onChange, testId }) => {
  const [uploading, setUploading] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadImage(file);
      onChange(url);
      toast.success("Image uploaded — remember to Save changes.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };
  return (
    <Field label={label}>
      <div className="flex gap-2">
        <input
          className={inputCls}
          data-testid={testId}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Image URL or upload →"
        />
        <label
          data-testid={`${testId}-upload-btn`}
          className={`flex shrink-0 cursor-pointer items-center gap-1.5 rounded-lg border border-[#00F0FF]/40 px-3.5 text-xs font-bold text-[#00F0FF] transition-colors duration-200 hover:bg-[#00F0FF]/10 ${uploading ? "opacity-60" : ""}`}
        >
          {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
          {uploading ? "Uploading…" : "Upload"}
          <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" data-testid={`${testId}-file-input`} onChange={pick} disabled={uploading} />
        </label>
      </div>
      {value && (
        <img src={imgSrc(value)} alt={`${label} preview`} className="mt-3 h-32 w-full rounded-xl border border-white/10 object-cover" data-testid={`${testId}-preview`} />
      )}
    </Field>
  );
};

const SaveBar = ({ saving, onSave, testId }) => (
  <button
    onClick={onSave}
    disabled={saving}
    data-testid={testId}
    className="flex items-center gap-2 rounded-full bg-[#00F0FF] px-7 py-3 text-sm font-bold text-[#050811] transition-shadow duration-300 hover:shadow-[0_0_28px_rgba(0,240,255,0.55)] disabled:opacity-60"
  >
    {saving && <Loader2 size={15} className="animate-spin" />}
    {saving ? "Saving…" : "Save changes"}
  </button>
);
